<template>
  <div class="engagement-mix">
    <div class="engagement-mix__heading">
      <h2 class="global-h4 engagement-mix__title">Engagement mix</h2>
      <NetworkIcons v-if="props.channels.length" :channels="props.channels" :size="18" />
    </div>

    <p v-if="!total" class="engagement-mix__empty">No engagement yet</p>

    <div v-else class="engagement-mix__list">
      <div v-for="item in items" :key="item.label" class="engagement-mix__row">
        <span class="engagement-mix__icon">
          <IconEmbedded :name="item.icon" :size="16" color="#667085" />
        </span>
        <span class="engagement-mix__label">{{ item.label }}</span>
        <span class="engagement-mix__value">{{ item.value }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import NumberFormatting from "@logic/NumberFormatting";

// Components
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import NetworkIcons from "./NetworkIcons.vue";

// Types
import type { IconName } from "@contracts/IconName";
import type { LocationSocialMetricsEngagementMix, SocialReportChannel } from "@contracts/socialReports";

const props = withDefaults(
  defineProps<{
    engagementMix: LocationSocialMetricsEngagementMix;
    /** Networks these engagements came from; shown as small logos in the header. */
    channels?: SocialReportChannel[];
  }>(),
  { channels: () => [] }
);

const total = computed<number>(() => {
  const mix = props.engagementMix;
  if (!mix) return 0;
  return (mix.likes ?? 0) + (mix.comments ?? 0) + (mix.shares ?? 0) + (mix.saves ?? 0);
});

const items = computed<{ label: string; icon: IconName; value: string }[]>(() => {
  const mix = props.engagementMix;
  return [
    // "Reactions" rather than "Likes" because Facebook reactions and Instagram likes are summed together.
    { label: "Reactions", icon: "heart_2", value: formatCount(mix?.likes ?? 0) },
    { label: "Comments", icon: "chat_2", value: formatCount(mix?.comments ?? 0) },
    { label: "Shares", icon: "share_2", value: formatCount(mix?.shares ?? 0) },
    { label: "Saves", icon: "save_2", value: formatCount(mix?.saves ?? 0) },
  ];
});

function formatCount(value: number): string {
  return NumberFormatting.abbreviatedFormat(value) || "0";
}
</script>

<style scoped lang="scss">
@import "@/scss/variables.scss";

// Engagement mix =============================================================
.engagement-mix {
  height: 100%;
  box-sizing: border-box;
  padding: 22px 24px;
  border: 1px solid #e4e7ec;
  border-radius: 11px;
  background: #fff;

  &__heading {
    margin-bottom: 16px;
    gap: 12px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
  }

  &__title {
    margin: 0;
  }

  &__empty {
    margin: 0;
    color: #667085;
    font: 400 14px/18px sans-serif;
  }

  &__list {
    gap: 13px;
    display: flex;
    flex-direction: column;
  }

  &__row {
    gap: 10px;
    display: flex;
    align-items: center;
  }

  &__icon {
    width: 26px;
    height: 26px;
    border-radius: 100px;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    flex-shrink: 0;
    background: #f4f6f8;
  }

  &__label {
    min-width: 0;
    flex-grow: 1;
    color: #667085;
    font: 400 14px/18px sans-serif;
  }

  &__value {
    flex-shrink: 0;
    color: #102f41;
    font: 600 15px/18px $font-family;
    text-align: right;
  }
}

// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .engagement-mix {
    padding: 18px 15px;
  }
}
</style>
